import { useCallback, useEffect, useMemo, useState } from "react";
import { Dialog } from "primereact/dialog";
import { toast } from "react-toastify";
import Button from "../../componentes/button/button";
import AtivoFormModal from "./AtivoFormModal";
import MovimentarAtivoModal from "./MovimentarAtivoModal";
import HistoricoAtivoModal from "./HistoricoAtivoModal";
import AnexosAtivoModal from "./AnexosAtivoModal";
import TermosAtivoModal from "./TermosAtivoModal";
import { gerarEtiquetas } from "./etiquetasPdf";
import { FASE_ESTILO, campoVisivel, exibirCampo, formatarData, formatarDataHora } from "./tiUtils";

/**
 * Detalhes do ativo em modal: dados por seção e equipamentos relacionados. Editar, Movimentar, Termos,
 * Etiqueta, Anexos e Histórico ficam no cabeçalho. Ao abrir um equipamento relacionado, o modal troca o
 * ativo exibido e permite voltar.
 */
export default function AtivoDetalheModal({ visible, ativoId, onHide, service, opcoes, onAlterado }) {
    const { metadados } = opcoes;

    const [pilha, setPilha] = useState([]);
    const [ativo, setAtivo] = useState(null);
    const [totalAnexos, setTotalAnexos] = useState(0);
    const [carregando, setCarregando] = useState(false);
    const [editando, setEditando] = useState(false);
    const [movimentando, setMovimentando] = useState(false);
    const [verHistorico, setVerHistorico] = useState(false);
    const [verAnexos, setVerAnexos] = useState(false);
    const [verTermos, setVerTermos] = useState(false);
    const [totalTermos, setTotalTermos] = useState(0);
    const [gerandoEtiqueta, setGerandoEtiqueta] = useState(false);

    const idAtual = pilha.length ? pilha[pilha.length - 1] : ativoId;

    useEffect(() => {
        if (visible) setPilha([]);
    }, [visible, ativoId]);

    const carregar = useCallback(async () => {
        if (!idAtual) return;
        setCarregando(true);
        try {
            const [a, an, te] = await Promise.all([
                service.buscarAtivo(idAtual),
                service.listarAnexos(idAtual),
                service.listarTermos(idAtual).catch(() => ({ data: [] })),
            ]);
            setAtivo(a.data);
            setTotalAnexos((an.data || []).length);
            setTotalTermos((te.data || []).length);
        } catch (e) {
            onHide();
        } finally {
            setCarregando(false);
        }
    }, [idAtual, service, onHide]);

    useEffect(() => {
        if (visible && idAtual) {
            setAtivo(null);
            carregar();
        }
        // recarrega só quando muda o ativo exibido
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, idAtual]);

    const aposAlteracao = async () => {
        await carregar();
        onAlterado?.();
    };

    const secoes = useMemo(() => {
        if (!ativo || !metadados) return [];
        const categoria = metadados.categorias.find(c => c.codigo === ativo.categoria);
        return metadados.secoes
            .map(s => ({
                ...s,
                campos: (categoria?.campos || []).filter(c => c.secao === s.codigo && campoVisivel(c, ativo)),
            }))
            .filter(s => s.campos.length > 0);
    }, [ativo, metadados]);

    const gerarEtiqueta = async () => {
        setGerandoEtiqueta(true);
        try {
            await gerarEtiquetas([ativo]);
        } catch (e) {
            toast.error("Não foi possível gerar a etiqueta.");
        } finally {
            setGerandoEtiqueta(false);
        }
    };

    const abrirRelacionado = (id) => setPilha(p => [...p, id]);
    const voltar = () => setPilha(p => p.slice(0, -1));

    const titulo = ativo
        ? ativo.nomenclatura || ativo.patrimonio || ativo.numeroSerie || `Ativo #${ativo.id}`
        : "Detalhes do ativo";

    const cabecalho = (
        <div className="flex flex-wrap items-center justify-between gap-3 w-full pr-2">
            <span className="font-semibold text-lg text-foreground break-all">{titulo}</span>
            {ativo && (
                <div className="flex flex-wrap gap-2">
                    {pilha.length > 0 && (
                        <Button label="Voltar" iconLeft="fa fa-arrow-left" variant="gray" onClick={voltar} />
                    )}
                    <Button label="Histórico" iconLeft="fa fa-history" variant="white" onClick={() => setVerHistorico(true)} />
                    <Button label={`Anexos (${totalAnexos})`} iconLeft="fa fa-paperclip" variant="white"
                        onClick={() => setVerAnexos(true)} />
                    <Button label={`Termos (${totalTermos})`} iconLeft="fa fa-file-contract" variant="white"
                        title="Gerar o termo de responsabilidade, registrar quem recebeu e consultar o documento assinado"
                        onClick={() => setVerTermos(true)} />
                    <Button label="Etiqueta / QR" iconLeft="fa fa-qrcode" variant="white" loading={gerandoEtiqueta}
                        title="Gera a etiqueta com QR Code e código de inventário para identificação física"
                        onClick={gerarEtiqueta} />
                    <Button label="Movimentar" iconLeft="fa fa-exchange-alt" variant="white" onClick={() => setMovimentando(true)} />
                    <Button label="Editar" iconLeft="fa fa-edit" variant="indigo" disabled={ativo.encerrado}
                        title={ativo.encerrado ? "Ativos baixados/descartados não podem ser editados" : undefined}
                        onClick={() => setEditando(true)} />
                </div>
            )}
        </div>
    );

    return (
        <Dialog
            header={cabecalho}
            visible={visible}
            onHide={onHide}
            blockScroll
            dismissableMask
            className="w-[98%] md:w-[1100px]"
            contentClassName="pt-3"
        >
            {!ativo || !metadados ? (
                <p className="text-sm text-muted-foreground">{carregando ? "Carregando..." : "Ativo não encontrado."}</p>
            ) : (
                <>
                    <div className="flex flex-wrap items-center gap-2 mb-4">
                        <span className="px-2.5 py-1 rounded-full text-xs font-mono font-semibold border border-border" title="Código de inventário (etiqueta/QR Code)">
                            {ativo.codigoInventario}
                        </span>
                        <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-muted">{ativo.categoriaRotulo}</span>
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${FASE_ESTILO[ativo.fase]}`}>
                            {ativo.statusNome}
                        </span>
                        {ativo.encerrado && (
                            <span className="text-xs text-muted-foreground">
                                Ativo encerrado — permanece nos relatórios; edições e movimentações estão bloqueadas.
                            </span>
                        )}
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                        {secoes.map(secao => (
                            <section key={secao.codigo} className="rounded-xl border border-border p-4">
                                <h3 className="text-sm font-semibold text-foreground mb-3">{secao.rotulo}</h3>
                                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
                                    {secao.campos.map(c => (
                                        <div key={c.chave} className={c.tipo === "TEXTO_LONGO" ? "sm:col-span-2" : ""}>
                                            <dt className="text-xs text-muted-foreground">{c.rotulo}</dt>
                                            <dd className="text-foreground break-words whitespace-pre-line">{exibirCampo(ativo, c)}</dd>
                                        </div>
                                    ))}
                                </dl>
                            </section>
                        ))}

                        <section className="rounded-xl border border-border p-4">
                            <h3 className="text-sm font-semibold text-foreground mb-3">Equipamentos relacionados</h3>
                            <p className="text-xs text-muted-foreground mb-1">Equipamento principal</p>
                            {ativo.equipamentoPrincipalId ? (
                                <button className="text-sm text-primary hover:underline mb-3"
                                    onClick={() => abrirRelacionado(ativo.equipamentoPrincipalId)}>
                                    {ativo.equipamentoPrincipalDescricao}
                                </button>
                            ) : <p className="text-sm mb-3">—</p>}

                            <p className="text-xs text-muted-foreground mb-1">Componentes vinculados ({ativo.componentes?.length || 0})</p>
                            {ativo.componentes?.length ? (
                                <ul className="text-sm space-y-1">
                                    {ativo.componentes.map(c => (
                                        <li key={c.id}>
                                            <button className="text-primary hover:underline" onClick={() => abrirRelacionado(c.id)}>
                                                {c.descricao}
                                            </button>
                                            <span className="text-muted-foreground"> · {c.tipoDispositivoNome} · {c.statusNome}</span>
                                        </li>
                                    ))}
                                </ul>
                            ) : <p className="text-sm">Nenhum componente vinculado.</p>}
                        </section>
                    </div>

                    <p className="mt-4 text-xs text-muted-foreground">
                        Cadastrado em {formatarData(ativo.criadoEm)} por {ativo.criadoPor || "—"} · Última atualização {formatarDataHora(ativo.atualizadoEm)}
                    </p>

                    <AtivoFormModal
                        visible={editando}
                        onHide={() => setEditando(false)}
                        service={service}
                        ativo={ativo}
                        opcoes={opcoes}
                        onSalvo={aposAlteracao}
                    />
                    <MovimentarAtivoModal
                        visible={movimentando}
                        onHide={() => setMovimentando(false)}
                        service={service}
                        ativo={ativo}
                        opcoes={opcoes}
                        onConcluido={aposAlteracao}
                    />
                    <HistoricoAtivoModal
                        visible={verHistorico}
                        onHide={() => setVerHistorico(false)}
                        service={service}
                        ativo={ativo}
                    />
                    <TermosAtivoModal
                        visible={verTermos}
                        onHide={() => setVerTermos(false)}
                        service={service}
                        ativo={ativo}
                        onAlterado={aposAlteracao}
                    />
                    <AnexosAtivoModal
                        visible={verAnexos}
                        onHide={() => setVerAnexos(false)}
                        service={service}
                        ativo={ativo}
                        tiposDocumento={metadados.tiposDocumento}
                        onAlterado={aposAlteracao}
                    />
                </>
            )}
        </Dialog>
    );
}
